import i18next from "i18next"
import {
	ContractRequest,
	ContractResponse,
	ErrorFn,
	ErrorResponse,
	Pack,
	QuoteCancelRequest,
	QuoteCancelResponse,
	QuoteRequest,
	QuoteResponse,
	RequestResponse,
} from "models"
import { catchError, getError, http } from "services/http"

export const getPacks = async (): Promise<{ res: Pack[] } & ErrorFn> => {
	const getPackErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "BicytrustNotEnabledException":
				return {
					code: error?.code,
					message: i18next.t("bicytrust_BicytrustNotEnabledException", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("bicytrust_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("search_error_default", { ns: "api" }),
				}
		}
	}

	const res = await http
		.get<Pack[]>(`obikeapi/bicytrust/packs`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as Pack[],
		getError: getError(res as unknown as ErrorResponse, getPackErrors),
	}
}

const getRequestErrors = (error: ErrorResponse) => {
	switch (error?.code) {
		case "BicytrustNotEnabledException":
			return {
				code: error?.code,
				message: i18next.t("bicytrust_BicytrustNotEnabledException", { ns: "api" }),
			}
		case "NotAuthorizedException":
		case "EntityStatusException":
			return {
				code: error?.code,
				message: i18next.t("bicytrust_error_NotAuthorizedException", { ns: "api" }),
			}
		default:
			return {
				code: error?.code,
				message: i18next.t("search_error_default", { ns: "api" }),
			}
	}
}

export const requestState = async (bikeId: string): Promise<{ res: RequestResponse } & ErrorFn> => {
	const res = await http
		.get<RequestResponse>(`obikeapi/bicytrust/request?bike_id=${bikeId}`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as RequestResponse,
		getError: getError(res as unknown as ErrorResponse, getRequestErrors),
	}
}

export const requestAllStates = async (): Promise<{ res: RequestResponse[] } & ErrorFn> => {
	const res = await http
		.get<string>(`obikeapi/bicytrust/requests`, {
			responseType: "stream",
		})
		.then((res) => {
			if (res.data === "null") return []
			// Backend teams return as single string
			// We must format the string to build javascript object
			const array = res.data.split("\n")
			const cleanedArray = array.reduce((acc, value) => {
				if (value !== "") {
					acc.push(JSON.parse(value))
				}
				return acc
			}, [])
			return cleanedArray
		})
	return {
		res: res as RequestResponse[],
		getError: getError(res as unknown as ErrorResponse, getRequestErrors),
	}
}

export const requestQuote = async (
	req: QuoteRequest
): Promise<{ res: QuoteResponse } & ErrorFn> => {
	const res = await http
		.post<QuoteResponse>(`obikeapi/bicytrust/request`, req, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as QuoteResponse,
		getError: getError(res as unknown as ErrorResponse, getRequestErrors),
	}
}

export const requestContract = async (
	req: ContractRequest
): Promise<{ res: ContractResponse } & ErrorFn> => {
	const res = await http
		.post<ContractResponse>(`obikeapi/bicytrust/request`, req, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as ContractResponse,
		getError: getError(res as unknown as ErrorResponse, getRequestErrors),
	}
}

export const requestCancel = async (
	req: QuoteCancelRequest
): Promise<{ res: QuoteCancelResponse } & ErrorFn> => {
	const res = await http
		.post<ContractResponse>(`obikeapi/bicytrust/request`, req, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as QuoteCancelResponse,
		getError: getError(res as unknown as ErrorResponse, getRequestErrors),
	}
}
